import React, { useState, useEffect } from 'react';
import { User } from '../../types';
import { Organization } from '../../services/api/adminOrganizationService';
import {
  TaskyButton,
  TaskyDialog,
  TaskyFormGroup,
  TaskyInput,
  TaskySelect,
} from '../tasky';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User;
  onSubmit: (userData: any) => void;
  organizations?: Organization[];
}

const UserFormModal: React.FC<UserFormModalProps> = ({ isOpen, onClose, user, onSubmit, organizations = [] }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tokenLimit, setTokenLimit] = useState<number | null>(null);
  const [tokensUsed, setTokensUsed] = useState<number | null>(null);
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ユーザーデータが提供された場合はフォームにセット
  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setPassword(''); // パスワードは既存値を表示しない
      setTokenLimit(user.token_limit || null);
      setTokensUsed(user.tokens_used || null);
      setOrganizationId(user.organization_id || null);
    } else {
      // 新規ユーザー作成時はフォームをリセット
      resetForm();
    }
  }, [user, isOpen]);

  // フォームリセット
  const resetForm = () => {
    setName('');
    setEmail('');
    setPassword('');
    setTokenLimit(null);
    setTokensUsed(null);
    setOrganizationId(null);
    setErrors({});
  };

  // 入力検証
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = '名前を入力してください';
    }

    if (!email.trim()) {
      newErrors.email = 'メールアドレスを入力してください';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = '有効なメールアドレスを入力してください';
    }

    // 新規ユーザー作成時のみパスワードを必須にする
    if (!user && !password) {
      newErrors.password = 'パスワードを入力してください';
    } else if (password && password.length < 8) {
      newErrors.password = 'パスワードは8文字以上必要です';
    }

    if (tokenLimit !== null && tokenLimit < 0) {
      newErrors.tokenLimit = 'クレジット上限は0以上の値を入力してください';
    }

    if (tokensUsed !== null && tokensUsed < 0) {
      newErrors.tokensUsed = '使用済みクレジットは0以上の値を入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // フォーム送信処理
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const userData: any = {
      name,
      email,
      ...(password && { password }),
      ...(tokenLimit !== null && { token_limit: tokenLimit }),
      ...(tokensUsed !== null && { tokens_used: tokensUsed }),
      organization_id: organizationId,
    };

    onSubmit(userData);
  };

  if (!isOpen) return null;

  return (
    <TaskyDialog
      open={isOpen}
      onClose={onClose}
      title={user ? 'ユーザー編集' : 'ユーザー作成'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <TaskyFormGroup label="名前" htmlFor="name" error={errors.name}>
          <TaskyInput
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ユーザー名"
            hasError={Boolean(errors.name)}
          />
        </TaskyFormGroup>

        <TaskyFormGroup label="メールアドレス" htmlFor="email" error={errors.email}>
          <TaskyInput
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="例: user@example.com"
            hasError={Boolean(errors.email)}
          />
        </TaskyFormGroup>

        <TaskyFormGroup
          label={
            <>
              パスワード{' '}
              {user && <span className="font-normal text-gray-400">（変更する場合のみ）</span>}
            </>
          }
          htmlFor="password"
          error={errors.password}
        >
          <TaskyInput
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={user ? '変更しない場合は空欄' : 'パスワード（8文字以上）'}
            hasError={Boolean(errors.password)}
          />
        </TaskyFormGroup>

        <TaskyFormGroup label="クレジット上限" htmlFor="tokenLimit" error={errors.tokenLimit}>
          <TaskyInput
            id="tokenLimit"
            type="number"
            value={tokenLimit === null ? '' : tokenLimit}
            onChange={(e) => setTokenLimit(e.target.value ? parseInt(e.target.value) : null)}
            placeholder="クレジット上限（無制限の場合は空欄）"
            hasError={Boolean(errors.tokenLimit)}
          />
        </TaskyFormGroup>

        <TaskyFormGroup label="使用済みクレジット" htmlFor="tokensUsed" error={errors.tokensUsed}>
          <TaskyInput
            id="tokensUsed"
            type="number"
            value={tokensUsed === null ? '' : tokensUsed}
            onChange={(e) => setTokensUsed(e.target.value ? parseInt(e.target.value) : null)}
            placeholder="使用済みクレジット数"
            hasError={Boolean(errors.tokensUsed)}
          />
        </TaskyFormGroup>

        <TaskyFormGroup label="所属組織" htmlFor="organizationId">
          <TaskySelect
            id="organizationId"
            value={organizationId ?? ''}
            onChange={(e) => setOrganizationId(e.target.value ? parseInt(e.target.value) : null)}
          >
            <option value="">なし</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </TaskySelect>
        </TaskyFormGroup>

        <div className="flex justify-end gap-3 pt-2">
          <TaskyButton type="button" variant="outline" size="sm" onClick={onClose}>
            キャンセル
          </TaskyButton>
          <TaskyButton type="submit" size="sm">
            {user ? '更新' : '作成'}
          </TaskyButton>
        </div>
      </form>
    </TaskyDialog>
  );
};

export default UserFormModal;
